import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Lightbulb, X, TrendingDown } from 'lucide-react';
import { PatternDetailsDialog } from './PatternDetailsDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DISMISS_KEY = 'seertech.smartDefaults.dismissedIds';
// Security audit (Feb 2026 · Batch 1): this key only stores an array
// of opaque "componentName::action" dismissal markers — no PII, no
// auth state, no IDs. `loadDismissed` already shape-checks the
// payload (rejects non-arrays, drops non-strings, caps len/count) so
// an XSS-injected blob can't leak through into render.
const ACCEPT_THRESHOLD = 60;   // % of clients who accept a remove proposal
const MIN_CR_COUNT = 2;        // require real signal

function loadDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    // Defence in depth: only accept arrays of plain strings — anything else
    // (object injection via XSS, malformed legacy value) gets thrown out
    // and the key is rewritten on next save. Caps length so a malicious
    // write can't balloon memory usage here.
    if (!Array.isArray(parsed)) return new Set();
    const clean = parsed.filter((v) => typeof v === 'string' && v.length < 200).slice(0, 500);
    return new Set(clean);
  } catch {
    return new Set();
  }
}
function saveDismissed(set) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}

/**
 * Inline banner at the top of Step 4 that turns aggregated CR acceptance
 * data into one-click adjustments to the current component selection.
 * Suggestions are dismissible (per-key, localStorage-backed).
 */
export function SmartDefaultsBanner({ selectedComponents, onDeselect }) {
  const [patterns, setPatterns] = useState(null);
  const [dismissed, setDismissed] = useState(() => loadDismissed());
  const [detailsFor, setDetailsFor] = useState(null); // component name when modal is open

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/scope-pack/change-patterns`, {
          params: { days: 365, min_votes: 2 }, withCredentials: true,
        });
        if (!cancelled) setPatterns(res.data);
      } catch {
        if (!cancelled) setPatterns(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const suggestions = useMemo(() => {
    if (!patterns) return [];
    const out = [];
    for (const row of patterns.most_accepted_changes || []) {
      if (row.proposed_action !== 'remove') continue;
      if (row.accept_rate < ACCEPT_THRESHOLD) continue;
      if (row.proposed < MIN_CR_COUNT) continue;
      const key = `${row.component_name}::remove`;
      if (dismissed.has(key)) continue;
      // Only suggest if the component is currently selected
      const isSelected = selectedComponents && Object.prototype.hasOwnProperty.call(selectedComponents, row.component_name);
      if (!isSelected) continue;
      out.push({ ...row, key });
    }
    return out.slice(0, 3);
  }, [patterns, selectedComponents, dismissed]);

  if (suggestions.length === 0) return null;

  const dismiss = (key) => {
    const next = new Set(dismissed); next.add(key);
    setDismissed(next); saveDismissed(next);
  };

  return (
    <Card className="border-amber-200 bg-amber-50/50 mb-4" data-testid="smart-defaults-banner">
      <CardContent className="py-3">
        <div className="flex items-start gap-3">
          <div className="shrink-0 mt-0.5">
            <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Lightbulb className="w-4 h-4" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-sm font-semibold text-[#0F2040]">Smart defaults</div>
              <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-800 font-normal">
                from your last {patterns?.window_days || 365}d of scopes
              </Badge>
            </div>
            <div className="space-y-2">
              {suggestions.map(s => (
                <div key={s.key}
                  className="flex flex-wrap items-center gap-2 bg-white border border-amber-100 rounded-md px-3 py-2"
                  data-testid={`smart-default-${s.component_name.replace(/\s/g, '-')}`}>
                  <TrendingDown className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <div className="flex-1 min-w-0 text-xs text-slate-700">
                    <span className="font-medium text-[#0F2040]">{s.component_name}</span>
                    {' '}is dropped in{' '}
                    <span className="font-semibold text-amber-700">{s.accept_rate}%</span>{' '}
                    of proposals ({s.accepted}/{s.proposed} accepted). Consider removing it here.
                    <button type="button"
                      onClick={() => setDetailsFor(s.component_name)}
                      className="ml-2 text-[11px] underline text-[#0F2040] hover:text-[#1A3055]"
                      data-testid={`smart-why-${s.component_name.replace(/\s/g, '-')}`}>
                      See the {s.accepted} accepted proposal{s.accepted === 1 ? '' : 's'}
                    </button>
                  </div>
                  <Button size="sm" variant="outline"
                    className="h-7 px-2 text-[11px] border-amber-500 text-amber-700 hover:bg-amber-50"
                    onClick={() => { onDeselect?.(s.component_name); dismiss(s.key); }}
                    data-testid={`smart-deselect-${s.component_name.replace(/\s/g, '-')}`}>
                    Remove
                  </Button>
                  <Button size="sm" variant="ghost"
                    className="h-7 px-2 text-[11px] text-slate-500 hover:text-slate-700"
                    onClick={() => dismiss(s.key)}
                    data-testid={`smart-keep-${s.component_name.replace(/\s/g, '-')}`}>
                    Keep
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <Button variant="ghost" size="sm"
            onClick={() => {
              const all = new Set(dismissed);
              suggestions.forEach(s => all.add(s.key));
              setDismissed(all); saveDismissed(all);
            }}
            className="shrink-0 h-6 w-6 p-0 text-slate-400 hover:text-slate-700"
            data-testid="smart-defaults-dismiss-all"
            title="Dismiss all">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardContent>
      <PatternDetailsDialog
        open={Boolean(detailsFor)}
        onOpenChange={(o) => !o && setDetailsFor(null)}
        componentName={detailsFor}
        action="remove"
        decision="accepted"
      />
    </Card>
  );
}
